'use client'

import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  buildToolLawnPrefill,
  chooseDefaultLawn,
  parseSavedLawnSize,
  type ToolLawnPrefill,
} from '@/lib/tool-lawn-prefill'

export interface UserLawn {
  id: string
  user_id: string
  name: string
  zip_code: string | null
  grass_type: string | null
  lawn_size: string | null
  sunlight: string | null
  soil_type: string | null
  latitude?: number | null
  longitude?: number | null
  city?: string | null
  state?: string | null
  created_at: string
  updated_at: string
}

interface UseDefaultLawnReturn {
  lawn: UserLawn | null
  lawns: UserLawn[]
  selectedLawnId: string | null
  setSelectedLawnId: (id: string) => void
  prefill: ToolLawnPrefill
  isLoading: boolean
  /**
   * False until the first lawn resolution completes: immediately after the
   * mount effect for anonymous users, or after the fetch settles for
   * authenticated users. `isLoading` alone cannot be used to detect "not yet
   * resolved" because it starts false, so a one-shot prefill that gates on it
   * would commit before the authenticated fetch even begins.
   */
  settled: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch the user's default lawn for pre-filling calculators and tools.
 * Returns the first (most recently created) lawn if the user is authenticated.
 *
 * Usage:
 * ```tsx
 * const { lawn, isLoading } = useDefaultLawn()
 *
 * // Pre-fill form with lawn data
 * if (lawn) {
 *   setGrassType(lawn.grass_type)
 *   setLawnSize(lawn.lawn_size)
 * }
 * ```
 */
export function useDefaultLawn(): UseDefaultLawnReturn {
  const { user } = useAuth()
  const [lawns, setLawns] = useState<UserLawn[]>([])
  const [selectedLawnId, setSelectedLawnId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [settled, setSettled] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchLawns = async () => {
    if (!user) {
      setLawns([])
      setSettled(true)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/user/lawns')

      if (!response.ok) {
        throw new Error('Failed to fetch lawns')
      }

      const data = await response.json()
      setLawns(data.lawns || [])
    } catch (err) {
      console.error('Error fetching lawns:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch lawns')
      setLawns([])
    } finally {
      setIsLoading(false)
      setSettled(true)
    }
  }

  useEffect(() => {
    fetchLawns()
    // Refetch on user change; fetchLawns is recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  useEffect(() => {
    if (lawns.length === 0) {
      setSelectedLawnId(null)
      return
    }
    if (!selectedLawnId || !lawns.some((lawn) => lawn.id === selectedLawnId)) {
      setSelectedLawnId(lawns[0].id)
    }
  }, [lawns, selectedLawnId])

  const defaultLawn = chooseDefaultLawn(lawns, selectedLawnId)

  return {
    lawn: defaultLawn,
    lawns,
    selectedLawnId: defaultLawn?.id ?? selectedLawnId,
    setSelectedLawnId,
    prefill: buildToolLawnPrefill(defaultLawn),
    isLoading,
    settled,
    error,
    refetch: fetchLawns
  }
}

/**
 * Helper to convert lawn size string to numeric square feet.
 * Handles both numeric strings and category names.
 */
export function parseLawnSize(lawnSize: string | null): number | undefined {
  return parseSavedLawnSize(lawnSize)
}

/**
 * Helper to get the current season based on date.
 */
export function getCurrentSeason(): 'spring' | 'summer' | 'fall' | 'winter' {
  const month = new Date().getMonth() // 0-11

  if (month >= 2 && month <= 4) return 'spring'   // Mar-May
  if (month >= 5 && month <= 7) return 'summer'   // Jun-Aug
  if (month >= 8 && month <= 10) return 'fall'    // Sep-Nov
  return 'winter'                                  // Dec-Feb
}
